import {makeAutoObservable, observable, reaction} from 'mobx';

import {Match, MatchesInitialDTO, User, UserDetailed} from '@/domain';
import {replaceObjectInArrayById} from '@/utils_legacy/replaceObjectInArrayById';

import {closeMatch} from '../lib/closeMatch';
import {LikedList, ViewedList} from './types';

interface ExtendedMatch extends Match {
  fetched?: string;
}

interface LikedOrViewedUser {
  list: Match;
  user: User;
}
interface NewMatch {
  match: Match | null;
  user: UserDetailed | null;
}
interface NewBump {
  user: User;
  matchId: number;
}

class MatchesStore {
  selectedChat: ExtendedMatch | null = null;
  matches: any = {
    list_new: observable([]),
    list_open: observable([]),
    users: observable([]),
    unreads: observable({visits: 0, reactions: 0, matches: 0}),
  };
  interactingUsersIds: number[] = [];

  liked_and_viewed: {
    liked: LikedList;
    viewed: ViewedList;
  } = {
    liked: {
      list: observable([]),
      users: observable([]),
      total_count: 0,
    },
    viewed: {
      list: observable([]),
      users: observable([]),
      visitors: observable([]),
      total_count: 0,
    },
  };
  newMatch: NewMatch = {
    match: null,
    user: null,
  };
  newUserMessage: number | null = null;
  newBumpUser: User | null = null;
  newBump: NewBump | null = null;
  newLikedUser: LikedOrViewedUser | null = null;
  bonusSuperSmash: number | null = null;
  reportUser: User | null = null;

  constructor() {
    makeAutoObservable(this);
    this.initializeReactions();
  }

  setIsUserInteracting(interacting: boolean, chatId: number) {
    if (interacting) {
      this.interactingUsersIds = [...this.interactingUsersIds, chatId];
    } else {
      this.interactingUsersIds = this.interactingUsersIds.filter(
        (id) => id !== chatId
      );
    }
  }

  setLikes(users: LikedList) {
    this.liked_and_viewed.liked = {
      list: users.list || [],
      users: users.users || [],
      total_count: users.total_count,
    };
  }

  removeUserFromLikes(userId: number) {
    const reactions = this.liked_and_viewed.liked.list.filter(
      (reaction) => reaction.user_id !== userId
    );

    this.setLikes({
      list: reactions,
      users: this.liked_and_viewed.liked.users.filter(
        (user) => user?.id !== userId
      ),
      total_count: reactions.length,
    });
  }

  setVisits(users: ViewedList) {
    this.liked_and_viewed.viewed = {
      list: users.list || [],
      users: users.users || [],
      visitors: users.visitors || [],
      total_count: users.total_count,
    };
  }

  removeUserFromVisits(visitorId: number) {
    const visits = this.liked_and_viewed.viewed.list.filter(
      (existVisit) => existVisit.user_id !== visitorId
    );

    this.setVisits({
      list: visits,
      users: this.liked_and_viewed.viewed.users.filter(
        (user) => user.id !== visitorId
      ),
      visitors: this.liked_and_viewed.viewed.visitors.filter(
        (visitor) => visitor.id !== visitorId
      ),
      total_count: visits.length,
    });
  }

  getMatchByUserId(userId: number): Match | undefined {
    return (
      this.matches.list_new.find((match: Match) => match.user_id === userId) ||
      this.matches.list_open.find((match: Match) => match.user_id === userId)
    );
  }

  setMatches(matches: MatchesInitialDTO) {
    this.matches = {
      ...this.matches,
      ...matches,
    };
  }

  unmatch(matchId: number, closeByUserId: number) {
    this.setMatches({
      ...this.matches,
      list_new: this.matches.list_new.map((match: Match) => {
        if (match.id === matchId) {
          return closeMatch(match, closeByUserId);
        }

        return match;
      }),
      list_open: this.matches.list_open.map((match: Match) => {
        if (match.id === matchId) {
          return closeMatch(match, closeByUserId);
        }

        return match;
      }),
    });

    // Reset new match if it was the one being un-matched
    if (this.newMatch.match && this.newMatch.match.id === matchId) {
      this.setNewMatch({match: null, user: null});
    }
  }

  prependMatch({match, user}: {match: Match; user: User}) {
    this.setMatches({
      ...this.matches,
      list_new: [match, ...this.matches.list_new],
      users: [user, ...this.matches.users],
      unreads: {
        ...this.matches.unreads,
        matches: this.matches.unreads.matches + 1,
      },
    });
  }

  addMatch({match, user}: {match: Match; user: User}) {
    if (match.status === 'NEW') {
      this.setMatches({
        ...this.matches,
        list_new: [...this.matches.list_new, match],
        users: [...this.matches.users, user],
      });

      return;
    }

    this.setMatches({
      ...this.matches,
      list_open: [...this.matches.list_open, match],
      users: [...this.matches.users, user],
    });
  }

  setNewMatch(match: NewMatch) {
    this.newMatch = {
      match: match.match || null,
      user: match.user || null,
    };
  }

  setNewChat(chat: Match) {
    const isInNewList = this.matches.list_new.find(
      (el: Match) => el.id === chat.id
    );
    if (isInNewList) {
      this.matches.list_new = replaceObjectInArrayById(
        this.matches.list_new,
        chat
      );
    } else {
      this.matches.list_open = replaceObjectInArrayById(
        this.matches.list_open,
        chat
      );
    }
    this.setIsUserInteracting(false, chat.id);
  }

  setSelectedChat(chat: Match) {
    this.selectedChat = chat;
  }

  initializeReactions() {
    reaction(
      () =>
        this.matches.list_new.map((chat: Match) => chat.last_messages?.slice()),
      () => this.updateSelectedChat()
    );

    reaction(
      () =>
        this.matches.list_open.map((chat: Match) =>
          chat?.last_messages?.slice()
        ),
      () => this.updateSelectedChat()
    );
  }

  updateSelectedChat() {
    if (this.selectedChat) {
      const updatedChat = this.getMatchById(this.selectedChat.id);
      if (updatedChat) {
        this.selectedChat = updatedChat;
      }
    }
  }
  getMatchById(chatId: number) {
    return (
      this.matches.list_new.find((el: Match) => el.id === chatId) ||
      this.matches.list_open.find((el: Match) => el.id === chatId)
    );
  }
  updateUnreadsMatches(count: number) {
    this.matches.unreads.matches = Math.max(0, count);
  }
  readAllMessages(chatId: number) {
    const match = this.getMatchById(chatId);
    if (!match) {
      return;
    }

    // mobx listener updates
    match.is_read = true;
    match.last_read_me_at = Date.now();
  }
  setNewUserMessage(message?: number) {
    this.newUserMessage = message || null;
  }
  setNewBump(bump: NewBump | null) {
    this.newBump = bump || null;
  }
  setNewBumpUser(user?: User) {
    this.newBumpUser = user || null;
  }
  setNewLikedUser(user?: LikedOrViewedUser) {
    this.newLikedUser = user || null;
  }
  setBonusSuperSmash(message?: number) {
    this.bonusSuperSmash = message || null;
  }
  setReportUser(user?: User) {
    this.reportUser = user || null;
  }
}

const matchesStore = new MatchesStore();
export default matchesStore;
